import React, { useCallback, useEffect, useState } from 'react';
import { alertsAPI } from '../services/api';
import './Alerts.css';

const getSeverityLabel = (alert) => (alert.severity || alert.risk || alert.priority || 'medium').toLowerCase();
const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});
  const [filters, setFilters] = useState({
    source: 'all',
    status: 'open',
    severity: '',
  });

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      const response = await alertsAPI.list(params);
      setAlerts(normalizeList(response.data, 'alerts'));
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.source, filters.status, filters.severity]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleAcknowledge = async (alertId) => {
    setActioning((prev) => ({ ...prev, [alertId]: true }));
    try {
      await alertsAPI.acknowledge(alertId);
      setAlerts((prev) => prev.map((alert) => (
        alert.id === alertId ? { ...alert, acknowledged: true, status: 'acknowledged' } : alert
      )));
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      setActioning((prev) => ({ ...prev, [alertId]: false }));
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="alerts-container">
        <div className="card">
          <h2 className="card-title">Alerts Center</h2>
          <p>Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alerts-container">
      <div className="alerts-header">
        <div>
          <h1>Alerts Center</h1>
          <p>Prioritized IDS and change detection events across monitored environments.</p>
        </div>
        <div className="alerts-filter-bar">
          <select value={filters.source} onChange={(event) => updateFilter('source', event.target.value)}>
            <option value="all">All Sources</option>
            <option value="ids">IDS</option>
            <option value="external">External</option>
          </select>
          <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
            <option value="open">Open</option>
            <option value="">All Statuses</option>
            <option value="acknowledged">Acknowledged</option>
          </select>
          <select value={filters.severity} onChange={(event) => updateFilter('severity', event.target.value)}>
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="card alerts-empty">
          <h2 className="card-title">No active alerts</h2>
          <p>New IDS detections and scan-to-scan changes will appear here as soon as they are detected.</p>
        </div>
      ) : (
        <div className="alerts-grid">
          {alerts.map((alert) => {
            const severity = getSeverityLabel(alert);
            const status = (alert.status || (alert.acknowledged ? 'acknowledged' : 'open')).toLowerCase();
            const isIdsAlert = alert.source === 'ids';
            return (
              <div key={alert.id || alert.created_at} className="card alert-card">
                <div className="alert-card-header">
                  <div>
                    <h2>{alert.title || 'Security Alert'}</h2>
                    <p className="alert-meta">
                      <span className={`badge badge-${severity}`}>{severity.toUpperCase()}</span>
                      <span className="alert-source">{(alert.source || 'external').toUpperCase()}</span>
                      <span className={`alert-status alert-status-${status}`}>{status.replace('_', ' ')}</span>
                      {alert.count > 1 && <span className="alert-count">{alert.count}x</span>}
                    </p>
                  </div>
                  {!alert.acknowledged && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleAcknowledge(alert.id)}
                      disabled={actioning[alert.id]}
                    >
                      {actioning[alert.id] ? 'Acknowledging...' : 'Acknowledge'}
                    </button>
                  )}
                </div>

                <div className="alert-content-grid">
                  <div>
                    <h3>What Changed</h3>
                    <p>{alert.what_changed || alert.change_summary || alert.summary || 'No change summary provided.'}</p>
                  </div>
                  <div>
                    <h3>Why It Matters</h3>
                    <p>{alert.why_it_matters || alert.impact || alert.risk_summary || 'No impact narrative provided.'}</p>
                  </div>
                  <div>
                    <h3>{isIdsAlert ? 'Network Flow' : 'Tenant'}</h3>
                    {isIdsAlert ? (
                      <p>
                        {alert.source_ip || 'unknown'}:{alert.source_port || '*'} -&gt;{' '}
                        {alert.destination_ip || 'unknown'}:{alert.destination_port || '*'} ({alert.protocol || 'unknown'})
                      </p>
                    ) : (
                      <p>{alert.tenant || alert.tenant_name || alert.location || 'Unassigned tenant'}</p>
                    )}
                  </div>
                  <div>
                    <h3>Evidence</h3>
                    {alert.evidence_link || alert.evidence_url ? (
                      <a
                        href={alert.evidence_link || alert.evidence_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="evidence-link"
                      >
                        View evidence
                      </a>
                    ) : isIdsAlert ? (
                      <p>Signature {alert.signature_id || 'unknown'} from {alert.interface || 'agent'}.</p>
                    ) : (
                      <p>No evidence link available.</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Alerts;
